/**
 * Catálogo centralizado de instrumentos con ítems propios (PCL-10, RFB-SJ).
 * Textos exactos para UI y referencia; scoring en lib.
 */

import type { TestItem } from '@/config/testItems';

// ─── PCL-10 ───
export const PCL_10_ID = 'PCL_10' as const;
export const PCL_10_NAME_RH = 'Perfil de Confiabilidad Laboral (PCL-10)';

export const PCL_10_LIKERT_OPTIONS = [
  { value: 1, label: 'Totalmente en desacuerdo' },
  { value: 2, label: 'En desacuerdo' },
  { value: 3, label: 'Ni de acuerdo ni en desacuerdo' },
  { value: 4, label: 'De acuerdo' },
  { value: 5, label: 'Totalmente de acuerdo' },
];

// ─── EEL-12 (Autogestión Emocional en el Trabajo) ───
export const EEL_12_ID = 'EEL_12' as const;
export const EEL_12_LIKERT_OPTIONS = PCL_10_LIKERT_OPTIONS;

export const EEL_12_ITEMS: TestItem[] = [
  { id: 'e1', text: 'Cuando hay cambios de último momento, logro adaptarme sin alterarme de forma significativa.', type: 'likert', options: EEL_12_LIKERT_OPTIONS },
  { id: 'e2', text: 'Si alguien me corrige en el trabajo, suelo mantener la calma y enfocarme en mejorar.', type: 'likert', options: EEL_12_LIKERT_OPTIONS },
  { id: 'e3', text: 'Después de un contratiempo, suelo recuperar la concentración y continuar con lo que sigue.', type: 'likert', options: EEL_12_LIKERT_OPTIONS },
  { id: 'e4', text: 'Cuando algo sale mal, me cuesta dejarlo atrás y sigo pensando en eso mientras trabajo.', type: 'likert', options: EEL_12_LIKERT_OPTIONS, reverse: true },
  { id: 'e5', text: 'Si un compañero o cliente está difícil, puedo mantener un trato respetuoso sin "engancharme".', type: 'likert', options: EEL_12_LIKERT_OPTIONS },
  { id: 'e6', text: 'En situaciones tensas, suelo elegir mis palabras antes de responder.', type: 'likert', options: EEL_12_LIKERT_OPTIONS },
  { id: 'e7', text: 'Bajo presión, a veces respondo "en caliente" y luego pienso que pude manejarlo mejor.', type: 'likert', options: EEL_12_LIKERT_OPTIONS, reverse: true },
  { id: 'e8', text: 'Aunque esté estresado, suelo pensar antes de responder o actuar.', type: 'likert', options: EEL_12_LIKERT_OPTIONS },
  { id: 'e9', text: 'Aunque tenga un mal momento, puedo sostener el ritmo de trabajo sin que se note demasiado en mis resultados.', type: 'likert', options: EEL_12_LIKERT_OPTIONS },
  { id: 'e10', text: 'En días pesados, mi estado de ánimo termina afectando la calidad o la velocidad con la que trabajo.', type: 'likert', options: EEL_12_LIKERT_OPTIONS, reverse: true },
  { id: 'e11', text: 'Cuando tengo varias demandas al mismo tiempo, logro mantener claridad y priorizar sin perder el control.', type: 'likert', options: EEL_12_LIKERT_OPTIONS },
  { id: 'e12', text: 'Cuando me siento presionado, reacciono de forma impulsiva (por ejemplo: digo cosas de más o actúo sin pensar).', type: 'likert', options: EEL_12_LIKERT_OPTIONS, reverse: true },
];

// ─── Mini-IPIP (20) — Perfil Integral de Personalidad Profesional ───
export const MINI_IPIP_20_ID = 'MINI_IPIP_20' as const;
export const MINI_IPIP_20_LIKERT_OPTIONS = [
  { value: 1, label: 'Totalmente en desacuerdo' },
  { value: 2, label: 'En desacuerdo' },
  { value: 3, label: 'Ni de acuerdo ni en desacuerdo' },
  { value: 4, label: 'De acuerdo' },
  { value: 5, label: 'Totalmente de acuerdo' },
];

const M = MINI_IPIP_20_LIKERT_OPTIONS;

export const MINI_IPIP_20_ITEMS: TestItem[] = [
  { id: 'm1', text: 'Me organizo con facilidad para cumplir pendientes.', type: 'likert', options: M },
  { id: 'm2', text: 'Me cuesta mantener la calma cuando hay presión.', type: 'likert', options: M, reverse: true },
  { id: 'm3', text: 'Disfruto convivir y conversar con personas nuevas.', type: 'likert', options: M },
  { id: 'm4', text: 'Procuro tratar a los demás con respeto, aun si no estoy de acuerdo.', type: 'likert', options: M },
  { id: 'm5', text: 'Me interesa aprender cosas nuevas aunque no sean necesarias para la tarea.', type: 'likert', options: M },
  { id: 'm6', text: 'Suelo posponer tareas aunque sean importantes.', type: 'likert', options: M, reverse: true },
  { id: 'm7', text: 'Me estreso con facilidad ante imprevistos.', type: 'likert', options: M, reverse: true },
  { id: 'm8', text: 'Prefiero mantenerme al margen en reuniones o grupos.', type: 'likert', options: M, reverse: true },
  { id: 'm9', text: 'A veces soy duro o brusco al decir las cosas.', type: 'likert', options: M, reverse: true },
  { id: 'm10', text: 'Me cuesta adaptarme a cambios en la forma de trabajar.', type: 'likert', options: M, reverse: true },
  { id: 'm11', text: 'Termino lo que empiezo, incluso cuando se vuelve pesado.', type: 'likert', options: M },
  { id: 'm12', text: 'Generalmente me recupero rápido después de un contratiempo.', type: 'likert', options: M },
  { id: 'm13', text: 'Se me facilita tomar la iniciativa para hablar o participar.', type: 'likert', options: M },
  { id: 'm14', text: 'Me importa cooperar y no afectar el trabajo de otros.', type: 'likert', options: M },
  { id: 'm15', text: 'Me gusta explorar alternativas y proponer mejoras.', type: 'likert', options: M },
  { id: 'm16', text: 'Me distraigo fácilmente y pierdo detalles.', type: 'likert', options: M, reverse: true },
  { id: 'm17', text: 'Me irrito o me altero con facilidad.', type: 'likert', options: M, reverse: true },
  { id: 'm18', text: 'Me cansa socializar y prefiero evitarlo.', type: 'likert', options: M, reverse: true },
  { id: 'm19', text: 'Me cuesta ser paciente cuando otros cometen errores.', type: 'likert', options: M, reverse: true },
  { id: 'm20', text: 'Prefiero lo conocido y evito probar métodos nuevos.', type: 'likert', options: M, reverse: true },
];

export const PCL_10_ITEMS: TestItem[] = [
  { id: 'p1', text: 'En la práctica, cumplir todas las reglas del trabajo no siempre es posible si se quiere ser eficiente.', type: 'likert', options: PCL_10_LIKERT_OPTIONS },
  { id: 'p2', text: 'Cuando un error no genera consecuencias reales, suele ser mejor seguir adelante que reportarlo.', type: 'likert', options: PCL_10_LIKERT_OPTIONS },
  { id: 'p3', text: 'Hay situaciones laborales en las que ajustarse estrictamente a los procedimientos complica innecesariamente el trabajo.', type: 'likert', options: PCL_10_LIKERT_OPTIONS },
  { id: 'p4', text: 'La mayoría de las personas sería más flexible con las normas si eso mejora los resultados.', type: 'likert', options: PCL_10_LIKERT_OPTIONS },
  { id: 'p5', text: 'Si una supervisión es muy estricta, es comprensible buscar atajos para cumplir con el trabajo.', type: 'likert', options: PCL_10_LIKERT_OPTIONS },
  { id: 'p6', text: 'En algunos trabajos, pequeñas omisiones no tienen impacto real en la confianza de la empresa.', type: 'likert', options: PCL_10_LIKERT_OPTIONS },
  { id: 'p7', text: 'La presión por resultados puede justificar decisiones que, en otro contexto, no serían aceptables.', type: 'likert', options: PCL_10_LIKERT_OPTIONS },
  { id: 'p8', text: 'Cuando las reglas no se aplican de forma consistente, es razonable interpretarlas de manera personal.', type: 'likert', options: PCL_10_LIKERT_OPTIONS },
  { id: 'p9', text: 'En el día a día laboral, la línea entre "lo correcto" y "lo práctico" no siempre es clara.', type: 'likert', options: PCL_10_LIKERT_OPTIONS },
  { id: 'p10', text: 'Muchas normas laborales están pensadas más para cubrir a la empresa que para facilitar el trabajo.', type: 'likert', options: PCL_10_LIKERT_OPTIONS },
];

// ─── RFB-SJ (8 ítems, opciones A/B/C/D) ───
export const RFB_SJ_8_ID = 'RFB_SJ_8' as const;
export const RFB_SJ_8_NAME_RH = 'Razonamiento Funcional (Juicio Situacional) — RFB-SJ';

export type RFBOptionLetter = 'A' | 'B' | 'C' | 'D';

export interface RFBItemDef {
  id: string;
  stem: string;
  options: { letter: RFBOptionLetter; text: string }[];
}

export const RFB_SJ_8_ITEMS: RFBItemDef[] = [
  {
    id: 'r1',
    stem: 'Estás terminando una tarea cuando tu supervisor te asigna otra urgente. Ambas son importantes y no puedes terminar las dos a tiempo.',
    options: [
      { letter: 'A', text: 'Avanzar lo más posible en ambas' },
      { letter: 'B', text: 'Priorizar la nueva tarea' },
      { letter: 'C', text: 'Terminar la que ya había iniciado' },
      { letter: 'D', text: 'Esperar a que el supervisor note el problema' },
    ],
  },
  {
    id: 'r2',
    stem: 'Un procedimiento indica una forma específica de hacer una tarea, pero tú conoces una manera más rápida que casi siempre funciona.',
    options: [
      { letter: 'A', text: 'Seguir el procedimiento tal como está' },
      { letter: 'B', text: 'Usar la forma rápida' },
      { letter: 'C', text: 'Combinar ambos enfoques' },
      { letter: 'D', text: 'Preguntar antes de decidir' },
    ],
  },
  {
    id: 'r3',
    stem: 'Un compañero con más experiencia te dice que "así se hace aquí", pero no coincide con lo que te indicaron oficialmente.',
    options: [
      { letter: 'A', text: 'Seguir al compañero' },
      { letter: 'B', text: 'Seguir la indicación oficial' },
      { letter: 'C', text: 'Confirmar con un superior' },
      { letter: 'D', text: 'Hacerlo como me parezca más lógico' },
    ],
  },
  {
    id: 'r4',
    stem: 'Detectas un error pequeño en tu trabajo justo antes de entregarlo, pero corregirlo implica retrasarte.',
    options: [
      { letter: 'A', text: 'Entregarlo así para cumplir el tiempo' },
      { letter: 'B', text: 'Corregirlo aunque se retrase' },
      { letter: 'C', text: 'Avisar y esperar indicaciones' },
      { letter: 'D', text: 'Dejarlo pasar si no afecta demasiado' },
    ],
  },
  {
    id: 'r5',
    stem: 'Te asignan varias tareas al mismo tiempo y no hay indicaciones claras sobre cuál es más importante.',
    options: [
      { letter: 'A', text: 'Hacer la más rápida' },
      { letter: 'B', text: 'Priorizar según impacto' },
      { letter: 'C', text: 'Preguntar qué va primero' },
      { letter: 'D', text: 'Empezar sin un orden claro' },
    ],
  },
  {
    id: 'r6',
    stem: 'Recibes una instrucción que no está del todo clara y no hay nadie disponible para aclararla.',
    options: [
      { letter: 'A', text: 'Decidir por mi cuenta' },
      { letter: 'B', text: 'Esperar a que alguien esté disponible' },
      { letter: 'C', text: 'Avanzar solo lo seguro' },
      { letter: 'D', text: 'Dejar la tarea pendiente' },
    ],
  },
  {
    id: 'r7',
    stem: 'Cuando ya llevas avanzado un trabajo, te informan que el enfoque debe cambiar.',
    options: [
      { letter: 'A', text: 'Continuar como iba' },
      { letter: 'B', text: 'Ajustar lo ya hecho' },
      { letter: 'C', text: 'Detenerme y replantear' },
      { letter: 'D', text: 'Esperar nuevas instrucciones' },
    ],
  },
  {
    id: 'r8',
    stem: 'Te piden realizar una tarea que no forma parte de tus funciones habituales.',
    options: [
      { letter: 'A', text: 'Rechazarla' },
      { letter: 'B', text: 'Hacerla sin preguntar' },
      { letter: 'C', text: 'Pedir orientación' },
      { letter: 'D', text: 'Ignorarla' },
    ],
  },
];

// ─── RA-10 (Responsabilidad y Autogestión) ───
export const RA_10_ID = 'RA_10' as const;
export type RA10OptionLetter = 'A' | 'B' | 'C';

export interface RA10ItemDef {
  id: string;
  stem: string;
  options: { letter: RA10OptionLetter; text: string }[];
}

export const RA_10_ITEMS: RA10ItemDef[] = [
  {
    id: 'ra1',
    stem: 'Cuando tengo varias tareas encima…',
    options: [
      { letter: 'A', text: 'Empiezo por lo más rápido para "desatorar", aunque no sea lo más importante.' },
      { letter: 'B', text: 'Me voy por partes y termino avanzando poco en todo.' },
      { letter: 'C', text: 'Me enfoco en una sola cosa y lo demás se empieza a acumular.' },
    ],
  },
  {
    id: 'ra2',
    stem: 'Si una tarea no está del todo clara…',
    options: [
      { letter: 'A', text: 'Avanzo como entiendo y luego ajusto si algo sale distinto.' },
      { letter: 'B', text: 'Me detengo hasta que me aclaren, aunque se pierda tiempo.' },
      { letter: 'C', text: 'Pregunto tarde porque me da pena "molestar" y ya voy atrasado.' },
    ],
  },
  {
    id: 'ra3',
    stem: 'Cuando nadie está encima supervisando…',
    options: [
      { letter: 'A', text: 'Me relajo y bajo el ritmo sin notarlo.' },
      { letter: 'B', text: 'Me cuesta decidir prioridades y me disperso más fácil.' },
      { letter: 'C', text: 'Me acelero para sacar todo y cometo más errores.' },
    ],
  },
  {
    id: 'ra4',
    stem: 'Si noto que no voy a llegar a tiempo…',
    options: [
      { letter: 'A', text: 'Intento resolverlo sin decir nada para no generar problema.' },
      { letter: 'B', text: 'Aviso cuando ya es evidente que no va a salir.' },
      { letter: 'C', text: 'Entrego algo incompleto para "cumplir" y luego lo arreglo.' },
    ],
  },
  {
    id: 'ra5',
    stem: 'Cuando el trabajo es repetitivo…',
    options: [
      { letter: 'A', text: 'Lo hago rápido para terminar, aunque se me vayan detalles.' },
      { letter: 'B', text: 'Lo pospongo porque me cuesta mantener constancia.' },
      { letter: 'C', text: 'Lo hago por ratos, pero pierdo el hilo y me desorganizo.' },
    ],
  },
  {
    id: 'ra6',
    stem: 'Si cometo un error en el trabajo…',
    options: [
      { letter: 'A', text: 'Lo corrijo sin decirlo para no quedar mal.' },
      { letter: 'B', text: 'Lo dejo un rato porque me frustra y lo evito.' },
      { letter: 'C', text: 'Lo minimizo y sigo, pensando que "no es para tanto".' },
    ],
  },
  {
    id: 'ra7',
    stem: 'Para no olvidar pendientes…',
    options: [
      { letter: 'A', text: 'Si no lo anoto, se me olvida con facilidad.' },
      { letter: 'B', text: 'Lo anoto, pero después no reviso lo que anoté.' },
      { letter: 'C', text: 'Confío en mi memoria y a veces se me van cosas.' },
    ],
  },
  {
    id: 'ra8',
    stem: 'Cuando me interrumpen mucho (mensajes, dudas, llamadas)…',
    options: [
      { letter: 'A', text: 'Contesto al momento y pierdo el ritmo.' },
      { letter: 'B', text: 'Las ignoro un rato y luego se me acumulan.' },
      { letter: 'C', text: 'Salto entre cosas y me cuesta cerrar pendientes.' },
    ],
  },
  {
    id: 'ra9',
    stem: 'En días de mucho movimiento…',
    options: [
      { letter: 'A', text: 'Puedo sostener el ritmo, pero dejo detalles para el final y a veces se me pasan.' },
      { letter: 'B', text: 'Me desordeno y termino haciendo lo urgente del momento.' },
      { letter: 'C', text: 'Me tenso y me cuesta mantener constancia.' },
    ],
  },
  {
    id: 'ra10',
    stem: 'Si debo trabajar sin un formato o ejemplo previo…',
    options: [
      { letter: 'A', text: 'Improviso y luego hay que rehacer parte.' },
      { letter: 'B', text: 'Me tardo mucho revisando opciones por miedo a equivocarme.' },
      { letter: 'C', text: 'Avanzo poco hasta que alguien me confirme cómo debe quedar.' },
    ],
  },
];

// ─── ML-8 (Motivadores Laborales, elección forzada A/B) ───
export const ML_8_ID = 'ML_8' as const;
export type ML8OptionLetter = 'A' | 'B';

export interface ML8ItemDef {
  id: string;
  options: { letter: ML8OptionLetter; text: string }[];
}

export const ML_8_ITEMS: ML8ItemDef[] = [
  {
    id: 'ml1',
    options: [
      { letter: 'A', text: 'Prefiero un trabajo estable y predecible, con cambios controlados.' },
      { letter: 'B', text: 'Prefiero un trabajo con retos nuevos, aunque cambie con frecuencia.' },
    ],
  },
  {
    id: 'ml2',
    options: [
      { letter: 'A', text: 'Me motiva más tener continuidad y seguridad en el puesto.' },
      { letter: 'B', text: 'Me motiva más aprender y avanzar, aunque implique más exigencia.' },
    ],
  },
  {
    id: 'ml3',
    options: [
      { letter: 'A', text: 'Si tengo que elegir, priorizo una mejor compensación aunque el ambiente sea neutral.' },
      { letter: 'B', text: 'Si tengo que elegir, priorizo un buen ambiente aunque la compensación sea solo adecuada.' },
    ],
  },
  {
    id: 'ml4',
    options: [
      { letter: 'A', text: 'Me engancho más cuando hay incentivos claros por resultados.' },
      { letter: 'B', text: 'Me engancho más cuando hay colaboración y buen trato diario.' },
    ],
  },
  {
    id: 'ml5',
    options: [
      { letter: 'A', text: 'Rindo mejor cuando me dan objetivos y me dejan decidir cómo lograrlos.' },
      { letter: 'B', text: 'Rindo mejor cuando hay instrucciones y lineamientos claros paso a paso.' },
    ],
  },
  {
    id: 'ml6',
    options: [
      { letter: 'A', text: 'Me gusta tomar decisiones sin estar consultando a cada rato.' },
      { letter: 'B', text: 'Me gusta confirmar criterios antes de actuar para evitar errores.' },
    ],
  },
  {
    id: 'ml7',
    options: [
      { letter: 'A', text: 'Prefiero tareas repetibles con procesos definidos.' },
      { letter: 'B', text: 'Prefiero tareas variadas donde no todo sea igual cada día.' },
    ],
  },
  {
    id: 'ml8',
    options: [
      { letter: 'A', text: 'Me siento cómodo dominando una rutina y perfeccionándola.' },
      { letter: 'B', text: 'Me siento cómodo adaptándome a situaciones distintas y cambiantes.' },
    ],
  },
];

// ─── APO-6 (Atención y Precisión Operativa) ───
export const APO_6_ID = 'APO_6' as const;
export type APO6OptionLetter = 'A' | 'B' | 'C';

export interface APO6ItemDef {
  id: string;
  stem: string;
  options: { letter: APO6OptionLetter; text: string }[];
  correctAnswer: APO6OptionLetter;
}

export const APO_6_ITEMS: APO6ItemDef[] = [
  {
    id: 'apo1',
    stem: 'Regla de validación (formato)\nRegla: 2 letras + 2 dígitos + "-" + 3 dígitos',
    options: [
      { letter: 'A', text: 'AB12-034' },
      { letter: 'B', text: 'A812-034' },
      { letter: 'C', text: 'AB1-2034' },
    ],
    correctAnswer: 'A',
  },
  {
    id: 'apo2',
    stem: 'Detección de discrepancia (con opciones gemelas)\nReferencia: QF-7081-ML\n¿Cuál opción NO coincide con la referencia?',
    options: [
      { letter: 'A', text: 'QF-7081-ML' },
      { letter: 'B', text: 'QF-7081-ML' },
      { letter: 'C', text: 'QF-7801-ML' },
    ],
    correctAnswer: 'C',
  },
  {
    id: 'apo3',
    stem: 'Orden alfabético (detalle fino)\nSelecciona la opción donde las letras están en orden alfabético (de A a Z).',
    options: [
      { letter: 'A', text: 'A C E G I' },
      { letter: 'B', text: 'A C E H I' },
      { letter: 'C', text: 'A C F G I' },
    ],
    correctAnswer: 'A',
  },
  {
    id: 'apo4',
    stem: 'Instrucción con excepción (O/0)\nSelecciona la opción que cumple TODAS:\n- Exactamente 6 caracteres\n- Solo letras y números\n- No puede iniciar con número\n- Debe contener al menos un 0 (cero)',
    options: [
      { letter: 'A', text: 'K2M9O7' },
      { letter: 'B', text: 'K2M907' },
      { letter: 'C', text: 'K2-M07' },
    ],
    correctAnswer: 'B',
  },
  {
    id: 'apo5',
    stem: 'Cálculo operativo\nProducto X: 6 cajas, 24 unidades por caja\nProducto Y: 4 cajas, 18 unidades por caja\n¿Cuál es el total de unidades X + Y?',
    options: [
      { letter: 'A', text: '204' },
      { letter: 'B', text: '216' },
      { letter: 'C', text: '228' },
    ],
    correctAnswer: 'B',
  },
  {
    id: 'apo6',
    stem: 'Doble condición\nElige la opción que cumple ambas:\n- Termina en número par\n- Tiene exactamente 2 letras (no más, no menos)',
    options: [
      { letter: 'A', text: 'RT18' },
      { letter: 'B', text: 'RTE18' },
      { letter: 'C', text: 'RT19' },
    ],
    correctAnswer: 'A',
  },
];
